import { CopyButton } from "../ui/CopyButton";
import { brandSelectionLabel } from "../../lib/brand-identity";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Brand } from "../../../../shared/types";

type Props = {
  brands: Brand[];
  activeBrand: Brand | undefined;
  activeBrandId: string | undefined;
  onChange: (id: string) => void;
  isLoading?: boolean;
  isError?: boolean;
};

// Show the search box once the list is long enough to risk overflowing the viewport.
const SEARCH_THRESHOLD = 7;

/** Case-insensitive name filter for the brand menu. Pure for easy testing. */
export function filterBrands(brands: Brand[], query: string): Brand[] {
  const q = query.trim().toLowerCase();
  return q ? brands.filter((b) => b.name.toLowerCase().includes(q)) : brands;
}

/**
 * Best-effort detection of throwaway smoke-test brands (e.g. "Layer2 Mobile
 * Smoke 2026-06-21T…", "ThinkBrowse Smoke …") that smoke scripts create and
 * don't always tear down, flooding the switcher (F2 dogfood finding).
 * Used for SORT ORDER ONLY — never to hide — so a false positive is harmless:
 * the brand still renders and search still finds it.
 */
export function looksLikeSmokeTestBrand(name: string | undefined): boolean {
  if (!name) return false;
  // "smoke" plus a generated unique suffix (ISO timestamp or epoch ms) — the
  // shape `uniqueName(...)` produces — to avoid de-prioritising a real brand
  // that merely has "smoke" in its name.
  return /\bsmoke\b/i.test(name) && /\d{4}-\d{2}-\d{2}|\b\d{10,}\b/.test(name);
}

/**
 * Order the menu so operators see real brands first: the active brand, then
 * non-test brands, then smoke-test brands last. Stable within each group.
 */
export function sortBrandsForMenu<T extends Brand>(brands: T[], activeBrandId: string | undefined): T[] {
  const rank = (b: T) =>
    b.id === activeBrandId ? 0 : looksLikeSmokeTestBrand(b.name) ? 2 : 1;
  return brands
    .map((b, i) => ({ b, i }))
    .sort((x, y) => rank(x.b) - rank(y.b) || x.i - y.i)
    .map(({ b }) => b);
}

/** Initials for the avatar — first letter of up to two words, else first two chars. */
function initials(name: string | undefined): string {
  if (!name) return "··";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
}

/**
 * Canonical rail brand switcher (design-system: rail = neutral-900, item_shape
 * rounded-lg, brand-600 accent). An avatar button at the top of the 60px rail that
 * opens a brand menu. This is the single brand switcher in the shell — the rail
 * "says where you are", so the active brand lives here.
 *
 * The menu caps its height and scrolls, and adds a search filter for long brand
 * lists (10+ brands overflowed the viewport — ThinkRun feedback). Option rows are
 * text-only (no per-row logo); duplicate names include full IDs.
 *
 * Switching NAVIGATES (the caller wires buildSwitchedBrandPath/buildBrandScopedPath)
 * because ActiveBrandContext.setActiveBrandId no-ops when id !== URL brandId.
 * See memory/feedback_brand_switch_must_navigate.md.
 */
export function RailBrandSwitcher({ brands, activeBrand, activeBrandId, onChange, isLoading = false, isError = false }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const listboxId = `${menuId}-list`;

  // Switching is optional; management discovery is available for every loaded list.
  const canSwitch = brands.length > 1;
  const showSearch = brands.length > SEARCH_THRESHOLD;
  // Real brands first (active → non-test → smoke-test last); search still matches all.
  const filtered = sortBrandsForMenu(filterBrands(brands, query), activeBrandId);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: globalThis.MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // On open: move focus into the menu (search box if present, else the
  // selected/first option) for keyboard users. The filter is cleared synchronously
  // by openMenu() so the first render never shows a stale filtered list.
  useEffect(() => {
    if (!open) return;
    if (showSearch) {
      searchRef.current?.focus();
      return;
    }
    const el = listRef.current;
    const selected = el?.querySelector<HTMLElement>('[aria-selected="true"]');
    (selected ?? el?.querySelector<HTMLElement>('[role="option"]'))?.focus();
  }, [open, showSearch]);

  function openMenu() {
    setQuery(""); // clear stale filter before the menu's first render
    setOpen(true);
  }

  function focusOption(index: number) {
    const opts = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    if (opts.length === 0) return;
    const i = ((index % opts.length) + opts.length) % opts.length;
    opts[i]?.focus();
  }

  function onListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const opts = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
    const idx = opts.indexOf(document.activeElement as HTMLElement);
    if (idx === -1) {
      focusOption(e.key === "ArrowDown" ? 0 : opts.length - 1);
      return;
    }
    // ArrowUp from the first option returns to the search box (natural escape hatch)
    // instead of wrapping to the last brand.
    if (idx === 0 && e.key === "ArrowUp" && showSearch) {
      searchRef.current?.focus();
      return;
    }
    focusOption(e.key === "ArrowDown" ? idx + 1 : idx - 1);
  }

  function onSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusOption(0);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        id={`${menuId}-trigger`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-label={isLoading
          ? "Loading brands — open brand menu"
          : isError
            ? "Brands unavailable — open brand menu"
            : `Active brand: ${activeBrand ? brandSelectionLabel(activeBrand, brands) : "none"} — open brand menu`}
        title={isLoading ? "Loading brands" : isError ? "Brands unavailable" : activeBrand ? brandSelectionLabel(activeBrand, brands) : "Select a brand"}
        onClick={() => {
          open ? setOpen(false) : openMenu();
        }}
        onKeyDown={(e) => {
          // ArrowDown opens the menu from the closed trigger (WAI-ARIA listbox pattern);
          // the focus-on-open effect then moves focus into the menu.
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            openMenu();
          }
        }}
        className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-600 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
      >
        {initials(activeBrand?.name)}
      </button>

      {open ? (
        <div className="absolute left-0 top-12 z-50 w-60 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg">
          <p className="px-2 py-1 font-data text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            {isLoading ? "Loading brands" : isError ? "Brands unavailable" : canSwitch ? "Switch brand" : activeBrand ? "Active brand" : "No active brand"}
          </p>
          {activeBrand && <details className="mx-2 mb-2 text-xs text-slate-600">
            <summary className="cursor-pointer">Active brand identity</summary>
            <p className="my-2 break-all">{activeBrand.id}</p>
            <CopyButton text={activeBrand.id} label="Copy brand ID" />
          </details>}
          {showSearch ? (
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder="Search brands…"
              aria-label="Search brands"
              aria-controls={listboxId}
              className="mb-1 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          ) : null}
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label="Switch brand"
            onKeyDown={onListKeyDown}
            className="max-h-64 overflow-y-auto"
          >
            {filtered.map((b) => {
              const selected = b.id === activeBrandId;
              const select = () => {
                setOpen(false);
                if (!selected) onChange(b.id);
              };
              return (
                // Canonical WAI-ARIA listbox option: non-interactive host (div) so the
                // listbox owns focus/keyboard; selection via click or Enter/Space.
                <div
                  key={b.id}
                  role="option"
                  tabIndex={-1}
                  aria-selected={selected}
                  onClick={select}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      select();
                    }
                  }}
                  className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-100 focus:bg-slate-100 focus:outline-none ${
                    selected ? "font-semibold text-slate-900" : "text-slate-700"
                  }`}
                >
                  <span className="min-w-0 flex-1 break-words" title={brandSelectionLabel(b, brands)}>{brandSelectionLabel(b, brands)}</span>
                  {selected ? (
                    <svg className="h-4 w-4 flex-none text-brand-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M20 6 9 17l-5-5" />
                    </svg>
                  ) : null}
                </div>
              );
            })}
          </div>
          {isLoading || isError || filtered.length === 0 ? (
            // Outside the listbox + a live region so AT announces it on filter change
            // (listbox children must be options).
            <p role="status" aria-live="polite" className="px-2 py-3 text-sm text-slate-400">
              {isLoading
                ? "Loading your brands…"
                : isError
                  ? "We couldn't load your brands. You can still manage brands or create one."
                  : brands.length === 0
                    ? "You have not created a brand yet."
                    : `No brands match “${query}”.`}
            </p>
          ) : null}
          <div className="mt-1 space-y-0.5 border-t border-slate-200 pt-1" aria-label="Brand management">
            <Link className="block rounded-lg px-2 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100" to="/brands" onClick={() => setOpen(false)}>
              Manage brands
            </Link>
            <Link className="block rounded-lg px-2 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50" to="/brands/new" onClick={() => setOpen(false)}>
              + Create brand
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
